import { z } from 'zod';

// --- Store ---

export const StoreSchema = z.object({
  id: z.string(), // store code, and the Firestore document id (e.g. "04521")
  name: z.string(),
  brand: z.string().nullable(),
  type: z.enum(['PHYSICAL', 'VIRTUAL']).nullable(),
});

export type Store = z.infer<typeof StoreSchema>;

export const StoreListResponseSchema = z.object({
  stores: z.array(StoreSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalCount: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  defaultStoreId: z.string().nullable().optional(),
  userId: z.string(),
});

export type StoreListResponse = z.infer<typeof StoreListResponseSchema>;

// --- Vendor ---

export const VendorSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),

  // Image Credits
  monthlyImageQuota: z.number().int().nonnegative(),
  currentImageMonthUsage: z.number().int().nonnegative(),
  currentImageBalance: z.number().int().nonnegative(),

  // Video Credits
  monthlyVideoQuota: z.number().int().nonnegative(),
  currentVideoMonthUsage: z.number().int().nonnegative(),
  currentVideoBalance: z.number().int().nonnegative(),

  // Free Image Credits (read-only)
  monthlyFreeImageQuota: z.number().int().nonnegative(),
  freeImageBalance: z.number().int().nonnegative(),

  isAllowedToHaveFreeCredits: z.boolean(),
});

export type Vendor = z.infer<typeof VendorSchema>;

// --- Vendor Quota Update (for batch PATCH) ---

export const VendorQuotaUpdateSchema = z.object({
  id: z.string(),
  monthlyImageQuota: z.number().int().nonnegative().optional(),
  monthlyVideoQuota: z.number().int().nonnegative().optional(),
  isAllowedToHaveFreeCredits: z.boolean().nullable().optional(),
});

export type VendorQuotaUpdate = z.infer<typeof VendorQuotaUpdateSchema>;

// --- Virtual Vendor (virtual store) ---

export const VirtualVendorSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  monthlyImageQuota: z.number().int().nonnegative(),
  currentImageMonthUsage: z.number().int().nonnegative(),
  currentImageBalance: z.number().int().nonnegative(),
  monthlyVideoQuota: z.number().int().nonnegative(),
  currentVideoMonthUsage: z.number().int().nonnegative(),
  currentVideoBalance: z.number().int().nonnegative(),
});

export type VirtualVendor = z.infer<typeof VirtualVendorSchema>;

export const VirtualVendorQuotaUpdateSchema = z.object({
  id: z.string(),
  monthlyImageQuota: z.number().int().nonnegative().optional(),
  monthlyVideoQuota: z.number().int().nonnegative().optional(),
});

export type VirtualVendorQuotaUpdate = z.infer<typeof VirtualVendorQuotaUpdateSchema>;

// --- Physical Vendor (physical store, subscription model) ---

export const PhysicalVendorSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  activeSubscription: z.boolean(),
  subscriptionPlanLabel: z.string().nullable(), // null when the vendor has no subscription
  subscriptionPrice: z.number().nonnegative(), // monthly price, 0 when no subscription
  subscriptionExpirationDate: z.string().nullable(), // ISO date-time; null unless a cancellation is scheduled
  freeImageBalance: z.number().int().nonnegative(), // remaining free image credits this month, 0 when subscribed
});

export type PhysicalVendor = z.infer<typeof PhysicalVendorSchema>;

// The backend returns a flat list of physical vendors with their subscription state.
export const PhysicalVendorListResponseSchema = z.array(PhysicalVendorSchema);

export type PhysicalVendorListResponse = z.infer<typeof PhysicalVendorListResponseSchema>;

// --- Subscription (physical store) ---

export const SubscriptionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  storeId: z.string(),
  createdAt: z.string(), // ISO date-time
  cancelled: z.boolean(),
  expirationDate: z.string().nullable(), // ISO date-time; null unless a cancellation is scheduled (1st of next civil quarter)
  subscriptionPlanLabel: z.string(),
  price: z.number().nonnegative(), // monthly cost
  quota: z.number().int().nonnegative(), // calls allowed on a rolling week
});

export type Subscription = z.infer<typeof SubscriptionSchema>;

// --- Subscription Plans (catalog, physical store) ---

export const SubscriptionPlanSchema = z.object({
  id: z.string(), // plan id (Firestore doc id), reusable as subscriptionPlanId on activation
  label: z.string(), // display label
  price: z.number().nonnegative(), // monthly price per vendor, in euros
  quota: z.number().int().nonnegative(), // calls allowed on a rolling week
});

export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

// The backend returns a flat list of available plans (V1: a single plan).
export const SubscriptionPlanListResponseSchema = z.array(SubscriptionPlanSchema);

export type SubscriptionPlanListResponse = z.infer<typeof SubscriptionPlanListResponseSchema>;

// --- Billing History (subscriptions, physical store) ---

export const BillingHistoryMonthSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  activeSubscriptions: z.number().int().nonnegative(),
  totalPrice: z.number().nonnegative(), // euros, already computed by the backend
  charged: z.boolean(), // true = month billed/closed, false = ongoing month
});

export type BillingHistoryMonth = z.infer<typeof BillingHistoryMonthSchema>;

// The backend returns a flat list of months (from the store's first subscription month to now).
export const BillingHistoryResponseSchema = z.array(BillingHistoryMonthSchema);

export type BillingHistoryResponse = z.infer<typeof BillingHistoryResponseSchema>;

// --- Usage History (consumption, virtual store) ---

export const UsageHistoryElementSchema = z.object({
  id: z.string(), // seller id
  firstName: z.string(),
  lastName: z.string(),
  imageUsage: z.number().int().nonnegative(),
  videoUsage: z.number().int().nonnegative(),
  totalPrice: z.number().nonnegative().optional(), // euros, frozen server-side; optional until the backend provides it
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
});

export type UsageHistoryElement = z.infer<typeof UsageHistoryElementSchema>;

// The backend returns a flat list: 5 sellers x 3 rolling months (requested month + 2 previous).
export const UsageHistoryResponseSchema = z.array(UsageHistoryElementSchema);

export type UsageHistoryResponse = z.infer<typeof UsageHistoryResponseSchema>;
